import { useState } from "react";
import client from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DataSourceBadge } from "@/components/DataSourceBadge";
import { Loader2, Wand2, TrendingDown, TrendingUp, Sparkles, Download } from "lucide-react";

const FILTERS = [
    { key: "losers", label: "Losers only" },
    { key: "top_movers", label: "Biggest movers" },
    { key: "all", label: "All keywords" },
];

/**
 * Bulk portfolio-level rank diagnosis. Sends all matching keywords + live SERP
 * competitor context to Claude in a single call, gets back a pattern-focused
 * report and per-keyword triage actions.
 */
export function BulkDiagnose({ open, onOpenChange, projectId }) {
    const [filter, setFilter] = useState("losers");
    const [limit, setLimit] = useState(20);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const run = async () => {
        setData(null);
        setErr(null);
        setLoading(true);
        try {
            const r = await client.post(`/projects/${projectId}/bulk-diagnose`, { filter, limit });
            setData(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Bulk diagnosis failed");
        } finally {
            setLoading(false);
        }
    };

    const download = () => {
        if (!data?.report) return;
        const md = `# Bulk SEO Diagnosis · ${data.project?.name || ""}\n\n_${new Date(data.generated_at).toLocaleString()}_\n\n${data.report}`;
        const blob = new Blob([md], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bulk-diagnosis-${(data.project?.domain || "site")}-${new Date().toISOString().slice(0, 10)}.md`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                data-testid="bulk-diagnose-sheet"
                side="right"
                className="w-full sm:max-w-2xl overflow-y-auto bg-background border-l border-border"
            >
                <SheetHeader>
                    <div className="overline flex items-center gap-2">
                        <Wand2 className="w-3 h-3" /> BULK AI DIAGNOSIS
                        {data && <DataSourceBadge source={data.data_source === "live" ? "dataforseo" : "mock"} />}
                    </div>
                    <SheetTitle className="font-display text-2xl tracking-tight">Diagnose the whole portfolio.</SheetTitle>
                    <SheetDescription className="text-muted-foreground text-sm">
                        Claude reads every matching keyword's live SERP and produces one triage report with a shared pattern and prioritized fixes.
                    </SheetDescription>
                </SheetHeader>

                {/* Filter row */}
                <div className="mt-6 flex flex-wrap items-center gap-2">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            data-testid={`bulk-filter-${f.key}`}
                            onClick={() => setFilter(f.key)}
                            className={`text-xs px-3 py-1.5 border ${filter === f.key ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <div className="ml-auto flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Max</label>
                        <input
                            type="number"
                            min={1}
                            max={30}
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            className="bg-input border border-border px-2 py-1 text-xs font-mono w-16 focus:outline-none focus:border-primary"
                        />
                        <button
                            data-testid="bulk-diagnose-run"
                            onClick={run}
                            disabled={loading}
                            className="bg-primary text-primary-foreground px-4 py-1.5 text-xs font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60"
                        >
                            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                            {loading ? "Diagnosing…" : "Run diagnosis"}
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="mt-10 flex flex-col items-center justify-center gap-3 py-16" data-testid="bulk-loading">
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        <div className="text-sm text-muted-foreground font-mono">Reading live SERPs and drafting the report…</div>
                        <div className="text-xs text-muted-foreground">This takes ~20 seconds for 20 keywords</div>
                    </div>
                )}

                {err && (
                    <div className="mt-6 text-sm text-destructive border border-destructive/40 bg-destructive/5 p-4" data-testid="bulk-error">
                        {err}
                    </div>
                )}

                {!loading && !data && !err && (
                    <div className="mt-10 border border-dashed border-border p-6 text-sm text-muted-foreground text-center">
                        Pick a filter and hit <span className="text-primary">Run diagnosis</span> to get started.
                    </div>
                )}

                {data && !loading && (
                    <div className="mt-6 space-y-6" data-testid="bulk-content">
                        {/* Keyword ledger */}
                        <div>
                            <div className="overline mb-2">Keywords analyzed ({data.keywords?.length || 0})</div>
                            <div className="border border-border max-h-72 overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="text-[10px] text-muted-foreground uppercase tracking-widest bg-card/40 sticky top-0">
                                        <tr>
                                            <th className="text-left p-2">Keyword</th>
                                            <th className="text-right p-2">Pos</th>
                                            <th className="text-right p-2">Δ</th>
                                            <th className="text-left p-2">Top competitor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.keywords || []).map((k) => (
                                            <tr key={k.id} className="border-t border-border/50">
                                                <td className="p-2 font-mono">{k.keyword}</td>
                                                <td className="p-2 text-right font-mono">{k.position}</td>
                                                <td className={`p-2 text-right font-mono ${k.change > 0 ? "text-success" : k.change < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                                                    <span className="inline-flex items-center gap-0.5">
                                                        {k.change > 0 ? <TrendingUp className="w-3 h-3" /> : k.change < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                                                        {k.change > 0 ? "+" : ""}{k.change}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-primary font-mono truncate max-w-[240px]">
                                                    {k.top_competitor?.domain || <span className="text-muted-foreground">—</span>}
                                                    {k.ai_overview && <span className="ml-1.5 inline-flex items-center gap-0.5 text-secondary text-[10px]"><Sparkles className="w-2.5 h-2.5" />AIO</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Report */}
                        {data.report && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="overline">Claude's triage report</div>
                                    <button onClick={download} className="text-xs text-primary hover:underline flex items-center gap-1">
                                        <Download className="w-3 h-3" /> Download markdown
                                    </button>
                                </div>
                                <pre data-testid="bulk-report"
                                     className="whitespace-pre-wrap font-sans text-sm text-foreground bg-card/40 border border-border p-5 leading-relaxed">
{data.report}
                                </pre>
                            </div>
                        )}

                        {!data.report && (
                            <div className="text-sm text-muted-foreground border border-border p-4">
                                AI generation temporarily unavailable. {data.ai_error && <span className="text-destructive/80 block mt-2 font-mono text-xs">{data.ai_error}</span>}
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
